'use client';
import styles from './navheader.module.scss';
import { NavDropdownContentProps, NavHeaderProps } from './NavHeader.type';
import { useCallback, useRef, useState, useEffect, Ref, HTMLAttributes } from 'react';
// TODO: dinamically import this once migrated into de-sxm-www
import { FocusTrap } from 'focus-trap-react';
import { NavLeftSection } from './NavHeaderComponents/NavLeftSection';
import { NavRightSection } from './NavHeaderComponents/NavRightSection';
import SearchBarContainer from './NavHeaderComponents/SearchBarContainer';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import cn from 'classnames';
import useClickOutside from '@/hooks/useClickOutside';
import { responsiveBreakpointsPx } from '@/constants';
import { isPopulatedNavDropdown } from './NavHeaderComponents/utils';
import { MobileDrawer } from './NavHeaderComponents/MobileDrawer';

export interface WidgetAttribute extends HTMLAttributes<HTMLElement> {
    class: string;
    ref?: Ref<HTMLElement>;
    'panel-margin-top': number;
    onClick: () => void;
}

interface UiAccountElement extends HTMLElement {
    closePanels: () => void;
}
declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements extends HTMLElement {
            'sxm-ui-nav-account-presence-icons': WidgetAttribute;
        }
    }
}
export function NavHeader({
    navLeftItems,
    helpCenterItem,
    trySiriusXMlink,
    appdownloadItem,
    theme = 'light',
    searchBar,
    search,
    account,
    onSearchClose,
    onSearchOpen,
    logo,
    logoUrl,
    avoidNavScroll = false,
    supportTitle,
    navRightCtas,
    navTheme,
    searchOpen = false,
    announcementBanners,
    headerId,
    overlap = true,
    preventScrollUp = false,
}: NavHeaderProps): React.JSX.Element {
    const SEARCH_PAGE_NAME = 'overlay:search';
    const MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR = '#onetrust-consent-sdk';
    const wrapperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const navRef = useRef<HTMLElement>(null);
    const navAndBannersRef = useRef<HTMLDivElement | null>(null);
    const resizeObserverRef = useRef<ResizeObserver>(null);
    const bannerRef = useRef<HTMLDivElement>(null);
    const [bannerHeight, setBannerHeight] = useState(0);

    // scroll range b/c OSX has negative scroll and bounce, this prevents nav flicker
    const { scrollDir, onPageTop } = useScrollDirection(0, -100, 10);
    const [helpCenterHovered, setHelpCenterHovered] = useState<boolean>(false);
    const [navDropdownHoveredIndex, setNavDropdownHoveredIndex] = useState<number | null>(null);
    const [popoverHoveredIndex, setPopoverHoveredIndex] = useState<number | null>(null);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [activeInnerDrawerIndex, setActiveInnerDrawerIndex] = useState<number | null>(null);
    const [isMobNavActive, setMobNavActive] = useState<boolean>(false);
    const accountWidget = useRef<UiAccountElement>(null);
    const [isDarkTheme, setIsDarkTheme] = useState(theme === 'dark');
    const [focusPause, setFocusPause] = useState(false);

    const hoverCloseTimeout = useRef<NodeJS.Timeout | null>(null);

    const scheduleHoverClose = () => {
        if (hoverCloseTimeout.current) clearTimeout(hoverCloseTimeout.current);
        hoverCloseTimeout.current = setTimeout(() => {
            setNavDropdownHoveredIndex(null);
            setPopoverHoveredIndex(null);
        }, 150);
    };

    const cancelHoverClose = () => {
        if (hoverCloseTimeout.current) {
            clearTimeout(hoverCloseTimeout.current);
            hoverCloseTimeout.current = null;
        }
    };

    useClickOutside(accountWidget, {}, () => {
        accountWidget?.current?.closePanels?.();
    });

    useClickOutside(wrapperRef, {}, (state, event) => {
        if (!(event instanceof MouseEvent) || !event.target) return;
        const target = event.target as HTMLElement;
        const closest = target.closest(MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR);
        setFocusPause(!!closest);
        if (!closest && searchOpen) {
            const searchResultsContainer = document.querySelector(`.${styles.searchContainer}`);
            if (searchResultsContainer) {
                const { right } = searchResultsContainer.getBoundingClientRect();
                if (event.clientX >= right - 15) {
                    return;
                }
            }
            const isInsideSearchResults = target.closest(`.${styles.searchContainer}`);
            if (!isInsideSearchResults) {
                onSearchClose?.();
            }
        }
    });

    useClickOutside(navAndBannersRef, {}, (state, event) => {
        if (!(event instanceof MouseEvent) || !event.target) return;
        const target = event.target as HTMLElement;
        const closest = target.closest(MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR);
        setFocusPause(!!closest);
        if (!closest && searchOpen) {
            const searchResultsContainer = document.querySelector(`.${styles.searchContainer}`);
            if (searchResultsContainer) {
                const { right } = searchResultsContainer.getBoundingClientRect();
                if (event.clientX >= right - 15) {
                    return;
                }
            }
            const isInsideSearchResults = target.closest(`.${styles.searchContainer}`);
            if (!isInsideSearchResults) {
                onSearchClose?.();
            }
        }
    });

    const toggleDocumentOverflow = useCallback(() => {
        const documentElement = document.documentElement;
        const isMobileNavWidth = documentElement.clientWidth < responsiveBreakpointsPx['nav-tablet'];

        if (isMobileNavWidth) {
            documentElement.style.overflow = searchOpen ? 'auto' : 'hidden';
        }
    }, [searchOpen]);

    const toggleSearchBar = () => {
        if (searchOpen) {
            onSearchClose?.();
        } else {
            onSearchOpen?.();
        }

        toggleDocumentOverflow();

        setNavDropdownHoveredIndex(null);
        setPopoverHoveredIndex(null);
    };

    const handleSearchClose = useCallback(() => {
        onSearchClose?.();
        toggleDocumentOverflow();
    }, [onSearchClose, toggleDocumentOverflow]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (theme === 'dark' && scrollDir === 'up' && !onPageTop) {
                setIsDarkTheme(false);
            } else if (theme === 'dark' && onPageTop && navDropdownHoveredIndex === null) {
                setIsDarkTheme(true);
            }
            if (navDropdownHoveredIndex !== null) {
                handleSearchClose();
            }
        }, 16);

        return () => clearTimeout(timeoutId);
    }, [onPageTop, scrollDir, theme, navDropdownHoveredIndex, handleSearchClose]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleSearchClose();
            }
        };

        if (searchOpen) {
            document.addEventListener('keyup', handleEscape);
            const searchOpenEvent = new CustomEvent('modalopen', {
                bubbles: true,
                detail: { pagename: SEARCH_PAGE_NAME },
            });
            document.body.dispatchEvent(searchOpenEvent);
        } else {
            document.removeEventListener('keyup', handleEscape);
            const searchCloseEvent = new CustomEvent('modalclose', { bubbles: true });
            document.body.dispatchEvent(searchCloseEvent);
        }

        return () => {
            document.removeEventListener('keyup', handleEscape);
        };
    }, [searchOpen, handleSearchClose]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        resizeObserverRef.current = new ResizeObserver(([entry]) => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const documentElement = document.documentElement;
                const isMobileNavWidth = documentElement.clientWidth < responsiveBreakpointsPx['nav-tablet'];

                if (isMobileNavWidth) {
                    // Mobile has different requirements so is handled via `handleDrawerToggle` and `toggleSearchBar` in
                    // this file
                    return;
                }
                const newNavHeight = entry?.borderBoxSize?.[0]?.blockSize;
                requestAnimationFrame(() => {
                    if (newNavHeight > documentElement.clientHeight) {
                        documentElement.style.overflow = 'hidden';
                    } else {
                        documentElement.style.overflow = 'auto';
                    }
                });
            }, 50);
        });

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        const scopedObserverRef = resizeObserverRef.current;

        if (scopedObserverRef && navRef.current) {
            requestAnimationFrame(() => {
                if (scopedObserverRef && navRef.current) {
                    scopedObserverRef.disconnect();
                    scopedObserverRef.observe(navRef.current);
                }
            });
        }
        return () => {
            document.documentElement.style.overflow = 'auto';
            scopedObserverRef?.disconnect();
        };
    }, [resizeObserverRef, navRef]);

    // We create the items for the mobile menu
    const activeInnerDrawer = (activeInnerDrawerIndex !== null && navLeftItems?.[activeInnerDrawerIndex]) || null;
    const navDropdownContentMobile = isPopulatedNavDropdown(activeInnerDrawer)
        ? {
              listItems:
                  activeInnerDrawer?.ctaSection?.buttonFields?.link_text &&
                  activeInnerDrawer?.ctaSection?.buttonFields?.href
                      ? [
                            {
                                linkText: activeInnerDrawer.ctaSection.buttonFields.link_text,
                                href: activeInnerDrawer.ctaSection.buttonFields.href,
                            },
                        ]
                      : [],
              tileContent: activeInnerDrawer?.tileSection,
          }
        : undefined;

    if (navDropdownContentMobile) {
        (activeInnerDrawer as NavDropdownContentProps)?.itemListSection?.forEach((item) =>
            navDropdownContentMobile.listItems.push({ linkText: item.title, href: item.href }),
        );
    }

    const handleDrawerToggle = () => {
        setMobNavActive(!isMobNavActive);
        const page = document.getElementsByTagName('html').item(0);
        if (page && !isMobNavActive) {
            page.style.overflow = 'hidden';
        } else if (page && isMobNavActive) {
            page.style.overflow = 'auto';
        }
    };

    useEffect(() => {
        if (!announcementBanners || !bannerRef.current) return;
        const ro = new ResizeObserver(([entry]) => {
            setBannerHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
        });
        ro.observe(bannerRef.current);
        return () => ro.disconnect();
    }, [announcementBanners]);

    useEffect(() => {
        if (navDropdownHoveredIndex !== null || searchOpen) {
            accountWidget?.current?.closePanels?.();
        }
    }, [navDropdownHoveredIndex, searchOpen]);
    const isSimpleType = !navLeftItems?.length && !navRightCtas?.length;
    const navFixed = preventScrollUp
        ? false
        : avoidNavScroll ||
          (scrollDir === 'none'
              ? false
              : scrollDir === 'up'
                ? !onPageTop && !searchOpen
                : navDropdownHoveredIndex !== null);

    const navTopOffset = navFixed && announcementBanners && (onPageTop || searchOpen) ? bannerHeight : 0;

    return (
        <>
            <FocusTrap
                active={searchOpen}
                paused={focusPause}
                focusTrapOptions={{
                    allowOutsideClick: true,
                }}
            >
                <div
                    className={cn(styles.topWrapper, {
                        [styles.noOverlap]: !overlap,
                        [styles.searchOpenTopWrapper]: searchOpen,
                    })}
                    ref={wrapperRef}
                >
                    {announcementBanners && (
                        <div
                            ref={bannerRef}
                            className={cn(styles.announcementBannerWrapper, {
                                [styles.bannerFixed]: navFixed,
                                [styles.bannerHidden]: !onPageTop && !searchOpen,
                                [styles.bannerSearchHidden]: searchOpen,
                            })}
                        >
                            {announcementBanners}
                        </div>
                    )}
                    <div ref={navAndBannersRef}>
                        <nav
                            ref={navRef}
                            style={navFixed ? { top: navTopOffset } : undefined}
                            className={cn(styles.wrapper, {
                                [styles.darktheme]: isDarkTheme,
                                [styles.navDropdownOpenStyles]: navDropdownHoveredIndex !== null || searchOpen,
                                [styles.searchOpenStyles]: searchOpen,
                                [styles.navFixed]: !searchOpen && navFixed,
                                [styles.headerBackground]:
                                    onPageTop && navDropdownHoveredIndex === null && !searchOpen && !avoidNavScroll,
                                [styles.whiteBackground]: avoidNavScroll,
                            })}
                            data-componenttype="Global Nav"
                        >
                            <div className={styles.navStrip}>
                                <NavLeftSection
                                    handleSearchClose={handleSearchClose}
                                    isDarkTheme={isDarkTheme}
                                    navDropdownHoveredIndex={navDropdownHoveredIndex}
                                    setHelpCenterHovered={setHelpCenterHovered}
                                    setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
                                    searchOpen={searchOpen}
                                    scheduleHoverClose={scheduleHoverClose}
                                    cancelHoverClose={cancelHoverClose}
                                    headerId={headerId}
                                    logo={logo}
                                    logoUrl={logoUrl}
                                    navLeftItems={navLeftItems}
                                    popoverHoveredIndex={popoverHoveredIndex}
                                    setPopoverHoveredIndex={setPopoverHoveredIndex}
                                />
                <NavRightSection
                    search={search}
                    searchOpen={searchOpen}
                    toggleSearchBar={toggleSearchBar}
                    handleSearchClose={handleSearchClose}
                                    buttonRef={buttonRef}
                    isDarkTheme={isDarkTheme}
                    account={account}
                    accountWidget={accountWidget}
                    appdownloadItem={appdownloadItem}
                    helpCenterItem={helpCenterItem}
                    supportTitle={supportTitle}
                                    helpCenterHovered={helpCenterHovered}
                                    setHelpCenterHovered={setHelpCenterHovered}
                                    navDropdownHoveredIndex={navDropdownHoveredIndex}
                                    setNavDropdownHoveredIndex={setNavDropdownHoveredIndex}
                                    trySiriusXMlink={trySiriusXMlink}
                                    navRightCtas={navRightCtas}
                                    navTheme={navTheme}
                                    onPageTop={onPageTop}
                                    isSimpleType={isSimpleType}
                                    setDrawerOpen={setDrawerOpen}
                                    handleDrawerToggle={handleDrawerToggle}
                                />
                            </div>
                            {search && (
                                <SearchBarContainer
                                    searchOpen={searchOpen}
                                    handleSearchClose={handleSearchClose}
                                    searchBar={searchBar}
                                    localpagename={SEARCH_PAGE_NAME}
                                />
                            )}
                        </nav>
                    </div>
                </div>
            </FocusTrap>
            <MobileDrawer
                isSimpleType={isSimpleType}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                handleDrawerToggle={handleDrawerToggle}
                navLeftItems={navLeftItems}
                supportTitle={supportTitle}
                setActiveInnerDrawerIndex={setActiveInnerDrawerIndex}
                activeInnerDrawerIndex={activeInnerDrawerIndex}
                navDropdownContentMobile={navDropdownContentMobile}
                helpCenterItem={helpCenterItem}
                trySiriusXMlink={trySiriusXMlink}
                navRightCtas={navRightCtas}
                appdownloadItem={appdownloadItem}
            />
            <div
                className={cn(styles.modalBackground, {
                    [styles.showModal]: navDropdownHoveredIndex !== null || drawerOpen || searchOpen,
                    [styles.showModalSearch]: searchOpen,
                })}
            ></div>
        </>
    );
}
